import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FilterDefinition} from "../../../../model/CourseFilters";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {forkJoin, of, Subscription, switchMap, tap} from "rxjs";
import {CourseFilterComponent} from 'src/app/utils/course-filter/course-filter.component';
import {SpinnerService} from "../../../../utils/services/spinner.service";
import {ReserveLessonComponent} from "../../../components/student/reserve-lesson/reserve-lesson.component";
import {ModalComponent} from "../../../../utils/modal/modal.component";
import {ProductContextService} from 'src/app/col/services/product-context.service';
import {ActivateCourseModalComponent} from 'src/app/utils/activate-course-modal/activate-course-modal.component';
import { NativeServiceApiProvider } from '../../../../services/native-api-provider.service';
import * as LessonsV3 from '../../../model/v3/lessons';
import {IntroductionState} from '../../../model/v3/lessons';

@Component({
    selector: 'app-reserve-lesson-page',
    templateUrl: './reserve-lesson-page.component.html',
    styleUrls: ['./reserve-lesson-page.component.scss'],
    standalone: false
})
export class ReserveLessonPageComponent implements OnInit, AfterViewInit, OnDestroy {

  private static modalIntroductionStates = [
    IntroductionState.Language,
    IntroductionState.Course,
    IntroductionState.PersonalProfile
  ];

  @ViewChild("reserveLessonComponent", {static: true}) reserveLessonComponent: ReserveLessonComponent
  @ViewChild('selectCourseModal', {static: true}) selectCourseModal: ModalComponent
  @ViewChild('welcomeModal', {static: true}) welcomeModal: ModalComponent
  @ViewChild('activateCourseModalComponent', {static: true}) activateCourseModalComponent: ActivateCourseModalComponent
  productContext: LessonsV3.ProductContextDetails | null = null
  productContextSubscription: Subscription
  scheduleDate: Date
  studentId: number
  schoolId: number
  personId: number
  introductionState: IntroductionState
  private isWelcomeModalActive = false
  CourseFilterComponent = CourseFilterComponent;
  private _chosenCourse: FilterDefinition
  activeCourse: FilterDefinition
  set chosenCourse(lang: FilterDefinition) {
    this._chosenCourse = lang
    if (this.shouldShowSelectCourseModal())
      this.selectCourseModal.show(false)
    this.refreshProductContext()
  }
  get chosenCourse() {
    return this._chosenCourse
  }

  constructor(private authService: AuthorizationServiceProvider,
              private spinner: SpinnerService,
              private cdr: ChangeDetectorRef,
              private apiProvider: NativeServiceApiProvider,
              private productContextService: ProductContextService) {}

  ngOnInit(): void {
    this.authService.getAuthDetailsService().pipe(
      switchMap(api => forkJoin({
        studentId: api.getSelfStudentId(),
        schoolId: api.getSelfSchoolId(),
        personId: api.getSelfPersonId(),
      })),
      tap(({ studentId, schoolId, personId }) => {
        this.studentId = studentId;
        this.schoolId = schoolId;
        this.personId = personId;
      }),
      switchMap(_ => this.apiProvider.colV3()),
      switchMap(api => api.getPersonColSettings(this.personId)),
      tap(profile => {
        this.introductionState = this.mapIntroductionState(profile?.introductionState);
        if (ReserveLessonPageComponent.modalIntroductionStates.includes(this.introductionState)) {
          this.isWelcomeModalActive = true
          this.selectCourseModal?.hide()
          this.welcomeModal.show(false)
        }
      }),
      switchMap(_ => this.getProductContext())
    ).subscribe()
    if(!this.chosenCourse || this.chosenCourse.code == 'ALL')
      this.spinner.trace(
        of(null)
      ).subscribe()
    this.productContextSubscription = this.productContextService.productContext.pipe(
      switchMap(_ => this.getProductContext()))
      .subscribe()
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    if (this.shouldShowSelectCourseModal())
      this.selectCourseModal.show(false)
  }

  ngOnDestroy(): void {
    if(this.productContextSubscription) this.productContextSubscription.unsubscribe()
  }

  getProductContext() {
    if (!this.schoolId || !this.studentId || !this.chosenCourse?.filter)
      return of(null);

    return this.apiProvider.colV3().pipe(
      switchMap((api) =>
        api.getProductContexts(this.schoolId, this.studentId, this.chosenCourse.filter)
      ),
      tap((contexts) => this.productContext = contexts?.[0] ?? null)
    )
  }

  applyCourse() {
    this.chosenCourse = this.activeCourse
    if(this.chosenCourse && this.chosenCourse?.code != 'ALL') {
      this.selectCourseModal.hide()
      this.welcomeModal?.hide()
      this.isWelcomeModalActive = false
    }
  }

  activateCourseFromWelcome() {
    this.applyCourse()
    if (this.activateCourseModalComponent && this.activeCourse?.filter && this.studentId) {
      this.apiProvider.colV3().pipe(
        switchMap((api) => api.updatePersonIntroductionState(this.personId, IntroductionState.Finished))
      ).subscribe()
      this.activateCourseModalComponent.studentId = this.studentId
      this.activateCourseModalComponent.langCode = this.activeCourse.filter
      this.activateCourseModalComponent.activateCourse()
  }
  }

  private isActivateCourseModalShown(): boolean {
    return !!this.activateCourseModalComponent?.activateCourseModal?.isShown()
  }

  private shouldShowSelectCourseModal(): boolean {
    return this.chosenCourse?.code == 'ALL' && !!this.selectCourseModal?.modal && !this.isActivateCourseModalShown() && !this.isWelcomeModalActive;
  }

  private refreshProductContext(): void {
    this.getProductContext().subscribe();
  }

  private mapIntroductionState(value?: string | null): IntroductionState {
    if (!value) {
      return IntroductionState.Course;
    }
    const enumValues = Object.values(IntroductionState) as string[];
    if (enumValues.includes(value)) {
      return value as IntroductionState;
    }
    return IntroductionState.Course;
  }
}
